/**
 * mapConstants.js — Shared constants, config, and utility functions for the MapboxMap component.
 */
import { Map as MapIcon, Mountain, Satellite } from 'lucide-react';
import DOMPurify from 'dompurify';

// POI type badge colors for popup display
export const POI_TYPE_COLORS = {
  fishing_access: '#2563EB',
  boat_ramp: '#0891B2',
  parking: '#6B7280',
  campsite: '#059669',
  trailhead: '#65A30D',
  restroom: '#8B5CF6',
  picnic_area: '#F59E0B',
  lodging: '#DC2626',
  'fly-shop': '#EA580C',
  dam: '#374151',
  waterfall: '#0EA5E9',
  stream_gauge: '#7C3AED',
  swimming_area: '#06B6D4',
  water_quality: '#10B981',
  other: '#3B82F6',
};

// POI type to icon image ID mapping
export const POI_ICON_MAP = {
  fishing_access: 'poi-fishing-access',
  boat_ramp: 'poi-boat-ramp',
  stream_gauge: 'poi-stream-gauge',
  rapids: 'poi-rapids',
  dam: 'poi-dam',
  waterfall: 'poi-waterfall',
  bridge: 'poi-bridge',
  hazard: 'poi-hazard',
  fly_shop: 'poi-fly-shop',
  lodging: 'poi-lodging',
  campsite: 'poi-campsite',
  trailhead: 'poi-trailhead',
  parking: 'poi-parking',
  restroom: 'poi-restroom',
  other: 'poi-other'
};

// Sanitize HTML for descriptions
export const sanitizeHTML = (html) => {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  });
};

export const containsHTML = (str) => {
  if (!str) return false;
  return /<[a-z][\s\S]*>/i.test(str);
};

// Get color for stream gauge condition
export const getConditionColor = (condition) => {
  const colorMap = {
    "very_low": "#f59e0b",
    "low": "#22c55e",
    "normal": "#3b82f6",
    "high": "#8b5cf6",
    "dangerous": "#ef4444",
    "unknown": "#9ca3af"
  };
  return colorMap[condition] || colorMap.unknown;
};

// Mini Sparkline SVG component for 7-day discharge trend
export const Sparkline = ({ data, width = 120, height = 32, color = "#3b82f6" }) => {
  if (!data || data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });

  const pathD = `M ${points.join(' L ')}`;
  const areaD = `M 0,${height} L ${points.join(' L ')} L ${width},${height} Z`;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <path d={areaD} fill={color} fillOpacity="0.1" />
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2}
        r="3"
        fill={color}
      />
    </svg>
  );
};

// Get trend icon and color
export const getTrendInfo = (trend) => {
  switch (trend) {
    case 'rising':
      return { icon: '\u2191', color: 'text-purple-600', label: 'Rising' };
    case 'falling':
      return { icon: '\u2193', color: 'text-green-600', label: 'Falling' };
    default:
      return { icon: '\u2192', color: 'text-gray-500', label: 'Stable' };
  }
};

// Mapbox style URLs
export const BASEMAPS = {
  streets: {
    name: "Streets",
    url: "mapbox://styles/mapbox/streets-v12",
    icon: MapIcon
  },
  outdoors: {
    name: "Outdoors",
    url: "mapbox://styles/mapbox/outdoors-v12",
    icon: Mountain
  },
  satellite: {
    name: "Satellite",
    url: "mapbox://styles/mapbox/satellite-streets-v12",
    icon: Satellite
  }
};

// Waterways tileset configuration
export const WATERWAYS_TILESET = 'mapbox://kensperry.us-waterways';
